import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../common/Modal';
import { useNotification } from '../../contexts/NotificationContext';
import { api, buildImageUrl } from '../../services/api';

interface WatermarkSettings {
  watermark_enabled: boolean;
  watermark_logo_path: string | null;
  watermark_position: string;
  watermark_opacity: number;
  watermark_size: number;
  watermark_margin: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

const Switch = styled.div<{ $enabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  .switch {
    width: 50px;
    height: 24px;
    background: ${({ $enabled, theme }) => ($enabled ? theme.colors.primary : '#ccc')};
    border-radius: 12px;
    position: relative;
    transition: background 0.3s;

    &::after {
      content: '';
      position: absolute;
      top: 2px;
      left: ${({ $enabled }) => ($enabled ? '26px' : '2px')};
      width: 20px;
      height: 20px;
      background: white;
      border-radius: 50%;
      transition: left 0.3s;
    }
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
`;

const RangeInput = styled.input`
  width: 100%;
  margin: 0.5rem 0;
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const RangeValue = styled.span`
  display: inline-block;
  min-width: 40px;
  text-align: center;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

const FileUploadArea = styled.div`
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 1.25rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.3s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  input {
    display: none;
  }
  label {
    cursor: pointer;
    display: block;
  }
`;

const PreviewArea = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.75rem;

  img {
    max-width: 120px;
    max-height: 80px;
    object-fit: contain;
    border-radius: 4px;
    border: 1px solid #ddd;
    background: #f8fafc;
    padding: 4px;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  border-top: 1px solid #eee;
  padding-top: 1.25rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'test' }>`
  padding: 0.7rem 1.5rem;
  border-radius: 6px;
  font-size: 0.95rem;
  cursor: pointer;
  border: none;
  font-weight: 500;
  transition: opacity 0.2s;

  background: ${({ $variant, theme }) =>
    $variant === 'secondary' ? '#e2e8f0' : $variant === 'test' ? '#28a745' : theme.colors.primary};
  color: ${({ $variant }) => ($variant === 'secondary' ? '#1e293b' : 'white')};

  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Hint = styled.small`
  color: #64748b;
  display: block;
  margin-top: 0.25rem;
`;

const WatermarkConfigModal: React.FC<Props> = ({ isOpen, onClose, onSaved }) => {
  const { showNotification } = useNotification();
  const [settings, setSettings] = useState<WatermarkSettings>({
    watermark_enabled: true,
    watermark_logo_path: null,
    watermark_position: 'bottom-right',
    watermark_opacity: 70,
    watermark_size: 15,
    watermark_margin: 20,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testImage, setTestImage] = useState<File | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/watermark/watermark-settings');
      const data = response.data || {};
      // Coalesce de nulos (tenants antigos podem ter colunas NULL no banco)
      setSettings({
        watermark_enabled: data.watermark_enabled ?? true,
        watermark_logo_path: data.watermark_logo_path ?? null,
        watermark_position: data.watermark_position ?? 'bottom-right',
        watermark_opacity: data.watermark_opacity ?? 70,
        watermark_size: data.watermark_size ?? 15,
        watermark_margin: data.watermark_margin ?? 20,
      });
    } catch (error) {
      showNotification("Erro ao carregar configurações da marca d'água", 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setLoading(true);
      const response = await api.post('/watermark/upload-watermark-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSettings((prev) => ({ ...prev, watermark_logo_path: response.data.logo_path }));
      showNotification('Logo enviado com sucesso!', 'success');
    } catch (error) {
      showNotification('Erro ao enviar o logo', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!testImage || !settings.watermark_logo_path) {
      showNotification('Selecione uma imagem de teste (e configure um logo)', 'error');
      return;
    }
    const formData = new FormData();
    formData.append('image', testImage);
    formData.append('logo_path', settings.watermark_logo_path);
    formData.append('position', settings.watermark_position);
    formData.append('opacity', String(settings.watermark_opacity ?? 70));
    formData.append('size_percentage', String(settings.watermark_size ?? 15));
    formData.append('margin', String(settings.watermark_margin ?? 20));
    try {
      setTesting(true);
      const response = await api.post('/watermark/test-watermark-with-path', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob',
      });
      const previewUrl = URL.createObjectURL(response.data);
      window.open(previewUrl, '_blank');
    } catch (error) {
      showNotification('Erro ao gerar o preview', 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    const formData = new FormData();
    formData.append('watermark_enabled', String(settings.watermark_enabled ?? true));
    formData.append('watermark_logo_path', settings.watermark_logo_path || '');
    formData.append('watermark_position', settings.watermark_position || 'bottom-right');
    formData.append('watermark_opacity', String(settings.watermark_opacity ?? 70));
    formData.append('watermark_size', String(settings.watermark_size ?? 15));
    formData.append('watermark_margin', String(settings.watermark_margin ?? 20));
    try {
      setSaving(true);
      await api.post('/watermark/update-watermark-settings', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showNotification('Configurações salvas com sucesso!', 'success');
      onSaved?.();
      onClose();
    } catch (error) {
      showNotification('Erro ao salvar as configurações', 'error');
    } finally {
      setSaving(false);
    }
  };

  const setField = <K extends keyof WatermarkSettings>(field: K, value: WatermarkSettings[K]) =>
    setSettings((prev) => ({ ...prev, [field]: value }));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Configurar marca d'água" size="md">
      <FormGroup>
        <Label>Marca d'água automática</Label>
        <Switch
          $enabled={settings.watermark_enabled}
          onClick={() => setField('watermark_enabled', !settings.watermark_enabled)}
        >
          <div className="switch" />
          <span>{settings.watermark_enabled ? 'Ativada' : 'Desativada'}</span>
        </Switch>
        <Hint>Aplicada automaticamente nas fotos enviadas em lote e nas imagens de imóveis.</Hint>
      </FormGroup>

      <FormGroup>
        <Label>Logo</Label>
        <FileUploadArea>
          <input type="file" accept="image/*" onChange={handleLogoUpload} id="wm-logo" />
          <label htmlFor="wm-logo">
            📎 Clique para enviar o logo
            <Hint>PNG com fundo transparente é o recomendado.</Hint>
          </label>
        </FileUploadArea>
        {settings.watermark_logo_path && (
          <PreviewArea>
            <img src={buildImageUrl(settings.watermark_logo_path)} alt="Logo atual" />
            <span>Logo atual</span>
          </PreviewArea>
        )}
      </FormGroup>

      <FormGroup>
        <Label>Posição</Label>
        <Select
          value={settings.watermark_position}
          onChange={(e) => setField('watermark_position', e.target.value)}
        >
          <option value="center">Centro</option>
          <option value="top-left">Superior esquerda</option>
          <option value="top-right">Superior direita</option>
          <option value="bottom-left">Inferior esquerda</option>
          <option value="bottom-right">Inferior direita</option>
        </Select>
      </FormGroup>

      <FormGroup>
        <Label>
          Opacidade: <RangeValue>{settings.watermark_opacity}%</RangeValue>
        </Label>
        <RangeInput
          type="range"
          min="10"
          max="100"
          value={settings.watermark_opacity}
          onChange={(e) => setField('watermark_opacity', Number(e.target.value))}
        />
      </FormGroup>

      <FormGroup>
        <Label>
          Tamanho: <RangeValue>{settings.watermark_size}%</RangeValue>
        </Label>
        <RangeInput
          type="range"
          min="5"
          max="50"
          value={settings.watermark_size}
          onChange={(e) => setField('watermark_size', Number(e.target.value))}
        />
      </FormGroup>

      <FormGroup>
        <Label>
          Margem: <RangeValue>{settings.watermark_margin}px</RangeValue>
        </Label>
        <RangeInput
          type="range"
          min="5"
          max="100"
          value={settings.watermark_margin}
          onChange={(e) => setField('watermark_margin', Number(e.target.value))}
        />
      </FormGroup>

      <FormGroup>
        <Label>Pré-visualizar (opcional)</Label>
        <FileUploadArea>
          <input
            type="file"
            accept="image/*"
            id="wm-test"
            onChange={(e) => setTestImage(e.target.files?.[0] || null)}
          />
          <label htmlFor="wm-test">
            🖼️ {testImage ? testImage.name : 'Selecione uma imagem para testar'}
          </label>
        </FileUploadArea>
        <div style={{ marginTop: '0.75rem' }}>
          <Button
            $variant="test"
            onClick={handleTest}
            disabled={testing || !testImage || !settings.watermark_logo_path}
          >
            {testing ? 'Gerando...' : 'Gerar preview'}
          </Button>
        </div>
      </FormGroup>

      <Actions>
        <Button $variant="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving || loading}>
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </Actions>
    </Modal>
  );
};

export default WatermarkConfigModal;
